import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LogOut, Menu, Calendar, MessageSquare, Image as ImageIcon, FileText, Settings, Users as UsersIcon, History } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BASE_TABS = [
  { to: "/admin/menus", label: "Menus", icon: Menu, tid: "admin-tab-menus" },
  { to: "/admin/hero", label: "Hero", icon: ImageIcon, tid: "admin-tab-hero" },
  { to: "/admin/content", label: "Content", icon: FileText, tid: "admin-tab-content" },
  { to: "/admin/reservations", label: "Reservations", icon: Calendar, tid: "admin-tab-reservations" },
  { to: "/admin/messages", label: "Messages", icon: MessageSquare, tid: "admin-tab-messages" },
  { to: "/admin/settings", label: "Settings", icon: Settings, tid: "admin-tab-settings" },
  { to: "/admin/changelog", label: "Changelog", icon: History, tid: "admin-tab-changelog" },
];

const ADMIN_ONLY_TABS = [
  { to: "/admin/users", label: "Users", icon: UsersIcon, tid: "admin-tab-users" },
];

export default function AdminShell() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = admin?.role === "admin";
  const tabs = isAdmin ? [...BASE_TABS, ...ADMIN_ONLY_TABS] : BASE_TABS;

  return (
    <div data-testid="admin-shell" className="min-h-screen bg-background text-foreground">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur-xl">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-primary text-lg leading-none">✦</span>
            <div>
              <div className="serif text-xl tracking-[0.22em] font-light">NELUM</div>
              <div className="text-[0.65rem] uppercase tracking-[0.22em] text-muted-foreground -mt-0.5">
                Admin Console
              </div>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <Link
              to="/"
              target="_blank"
              data-testid="admin-view-live"
              className="hidden sm:inline-flex text-[0.72rem] uppercase tracking-[0.22em] text-foreground/70 hover:text-primary transition-colors"
            >
              View live site ↗
            </Link>
            <span className="hidden md:inline text-xs text-muted-foreground" data-testid="admin-current-user">
              {admin?.email}
              {admin?.role && (
                <span
                  data-testid="admin-role-badge"
                  className={`ml-2 px-1.5 py-0.5 text-[0.55rem] uppercase tracking-[0.18em] font-semibold border ${
                    isAdmin
                      ? "border-primary/60 text-primary"
                      : "border-border text-muted-foreground"
                  }`}
                >
                  {admin.role}
                </span>
              )}
            </span>
            <button
              data-testid="admin-logout"
              onClick={() => { logout(); navigate("/admin/login"); }}
              className="inline-flex items-center gap-2 px-4 py-2 border border-border text-[0.72rem] uppercase tracking-[0.22em] text-foreground/85 hover:border-primary hover:text-primary transition-colors"
            >
              <LogOut size={12} strokeWidth={1.5} />
              Sign Out
            </button>
          </div>
        </div>

        {/* Sub-nav tabs */}
        <div className="border-t border-border">
          <div className="mx-auto max-w-[1400px] px-6 lg:px-12 flex items-stretch overflow-x-auto">
            {tabs.map(({ to, label, icon: Icon, tid }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={tid}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 py-4 px-5 text-[0.72rem] uppercase tracking-[0.22em] font-semibold border-b-2 transition-colors ${
                    isActive
                      ? "text-primary border-primary"
                      : "text-foreground/60 border-transparent hover:text-foreground"
                  }`
                }
              >
                <Icon size={13} strokeWidth={1.5} />
                {label}
              </NavLink>
            ))}
          </div>
        </div>
      </header>

      <Outlet />
    </div>
  );
}
